'use client';

import { useUser } from './User';
import { useAuthProvider } from '@modules/auth/providers/AuthProvider/AuthProvider';
import { Workspace, WorkerGroup } from '@modules/shared/api/v2/api';
import { workspaces as workspacesHooks } from '@modules/shared/api/v2/hooks';
import { useGetWorkspace } from '@modules/workspaces/shared/api/useGetWorkspace';

import { Toast } from '@capra/core';
import { useParams } from 'next/navigation';
import React, { useMemo, createContext, useContext, useState, useEffect, useCallback } from 'react';
import { AccountState } from 'shared/src/account/types';
import { TeamCompact } from 'shared/src/teams/types';
import { DEFAULT_WORKERGROUP_NAME } from 'shared/src/workspaces/constants';
import { KeyedMutator } from 'swr';

const PROVISION_STATE_FETCH_INTERVAL = 60000;
const HIBERNATION_STATE_FETCH_INTERVAL = 5000;

export type WorkspaceWithPathName = Workspace & {
  /** The client-side pathname for routing */
  pathname?: string;
  /** Conditionally embedded in the v2 list response for SSO-affiliated users */
  teams?: TeamCompact[];
};

export type IWorkspaceContext = WorkspaceWithPathName & {
  /**
   * Name of the workspace the app is currently scoped to. On a workspace route this is always the
   * `[workspaceName]` URL segment; elsewhere it falls back to the user's stored preference.
   */
  workspaceId: string | null;
  workspaces: WorkspaceWithPathName[];
  workerGroups: WorkerGroup[];
  isWorkspaceLoading: boolean;
  isWorkerGroupsLoading: boolean;
  selectedWorkerGroup: WorkerGroup;
  versionFull: string;
  setWorkerGroup: (name: string) => void;
  workspacesError?: Error;
  workerGroupsError?: Error;
  mutateWorkspaces: KeyedMutator<Workspace[]>;
  mutateWorkerGroups: KeyedMutator<WorkerGroup[]>;
  workspaceError: unknown;
  workspaceDetailError?: Error;
  error?: Error;
  setWorkspaceId: (id: string) => void;
};

const WorkspaceContext = createContext<IWorkspaceContext>({} as IWorkspaceContext);

export function useWorkspace(): IWorkspaceContext {
  const context = useContext(WorkspaceContext);

  if (context === undefined) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }

  return context;
}

// Stable, fetch-free context used before an org id is known (e.g. on the org-list page, or on the
// first render of any authenticated page before OrganizationAuthGuard resolves the org). Mirrors the
// empty shape the inner provider starts from; consumers that mutate workspace state only render
// inside the org route, where the inner provider supplies the real context.
const noop = (): void => {};
const EMPTY_WORKSPACE_CONTEXT = {
  workspaceId: null,
  workspaces: [],
  workerGroups: [],
  selectedWorkerGroup: {} as WorkerGroup,
  versionFull: '',
  isWorkspaceLoading: true,
  isWorkerGroupsLoading: true,
  setWorkerGroup: noop,
  setWorkspaceId: noop,
  mutateWorkspaces: (async () => undefined) as KeyedMutator<Workspace[]>,
  mutateWorkerGroups: (async () => undefined) as KeyedMutator<WorkerGroup[]>,
  workspaceError: undefined,
  workspaceDetailError: undefined,
} as unknown as IWorkspaceContext;

// The generated `useWorkspaces`/`useWorkspaceWorkergroups` hooks build their URL as
// `key ?? queryKeys.workspaces(orgId)`, so a `key: null` guard does NOT stop the fetch (null
// coalesces to the URL). Because this provider mounts ABOVE OrganizationAuthGuard, on a hard load
// it would otherwise call `useWorkspaces('')` and fire `/v2/organizations//workspaces` -> 403.
// Gate the data fetching behind a real org id: the inner component (which holds the SWR hooks) is
// only rendered once `authorizedOrganizationId` is set, while the outer provider supplies a stable
// empty context until then. Hooks cannot be called conditionally, so the split is required.
export const WorkspaceProvider = ({ children }: { children: React.ReactNode }) => {
  const { authorizedOrganizationId } = useAuthProvider();

  if (!authorizedOrganizationId) {
    return <WorkspaceContext.Provider value={EMPTY_WORKSPACE_CONTEXT}>{children}</WorkspaceContext.Provider>;
  }

  return (
    <WorkspaceProviderInner authorizedOrganizationId={authorizedOrganizationId}>{children}</WorkspaceProviderInner>
  );
};

const WorkspaceProviderInner = ({
  authorizedOrganizationId,
  children,
}: {
  authorizedOrganizationId: string;
  children: React.ReactNode;
}) => {
  const { updateUserPreferences, userPreferences } = useUser();
  // The `[workspaceName]` URL segment is the source of truth for the active workspace. This provider
  // mounts above that segment, so an effect in the child layout cannot push the name up while the
  // segment is suspended -- which is how the nav kept showing the old name. See PLAT-12029.
  const { workspaceName: workspaceNameParam } = useParams<{ workspaceName?: string }>();
  const workspaceNameFromUrl = Array.isArray(workspaceNameParam) ? workspaceNameParam[0] : workspaceNameParam;
  // Fallback for routes with no workspace segment.
  const [preferredWorkspaceId, setPreferredWorkspaceId] = useState<string | null>(
    userPreferences?.[authorizedOrganizationId]?.currentWorkspaceId || null,
  );
  const workspaceId: string | null = workspaceNameFromUrl || preferredWorkspaceId;

  // Retained for context API compatibility; there are currently no production callers.
  const setWorkspaceId = useCallback((id: string): void => {
    setPreferredWorkspaceId(id);
  }, []);

  const {
    data: workspaces,
    error: workspacesError,
    mutate: mutateWorkspaces,
  } = workspacesHooks.queries.useWorkspaces(authorizedOrganizationId, {
    revalidateOnFocus: false,
  });

  const {
    data: workerGroupsData,
    mutate: mutateWorkerGroups,
    error: workspaceError,
    isLoading: isWorkerGroupsLoading,
  } = workspacesHooks.queries.useWorkspaceWorkergroups(authorizedOrganizationId, workspaceId ?? '', {
    key: workspaceId ? undefined : null,
    revalidateOnFocus: false,
  });

  const { data: singleWorkspaceData, error: workspaceDetailError } = useGetWorkspace({
    organizationId: authorizedOrganizationId,
    workspaceId,
  });

  const workerGroups = useMemo(() => {
    if (!workerGroupsData) {
      return [];
    }

    return workerGroupsData.filter((wg) => wg.kind !== 'lake_access');
  }, [workerGroupsData]);

  const [selectedWorkerGroup, setSelectedWorkerGroup] = useState({} as WorkerGroup);

  const setWorkerGroup = useCallback(
    (workerGroupName: string): void => {
      const foundWorkerGroup = workerGroups?.find((workerGroup) => workerGroup.name === workerGroupName);

      if (foundWorkerGroup) {
        setSelectedWorkerGroup(foundWorkerGroup);
      } else {
        Toast.error('Unable to set worker group.', { position: 'bottom-right' });
        // oxlint-disable-next-line no-console -- intentional diagnostics next to the user-facing toast
        console.error(`Worker group ${workerGroupName} not found`, { workerGroups });
      }
    },
    [workerGroups],
  );

  useEffect(() => {
    if (!Array.isArray(workerGroups) || workerGroups.length === 0) {
      // This can happen because authorizedOrganizationId is null,
      // or if there are no worker groups returned from the API
      return;
    }

    // TODO: Track selected worker groups by workspace
    setSelectedWorkerGroup({} as WorkerGroup);
  }, [workspaceId, workerGroups]);

  useEffect(() => {
    if (workspaceId && authorizedOrganizationId) {
      updateUserPreferences({
        [authorizedOrganizationId]: {
          currentWorkspaceId: workspaceId,
        },
      });
    }
  }, [authorizedOrganizationId, workspaceId, updateUserPreferences]);

  useEffect(() => {
    if (!Array.isArray(workspaces) || workspaces.length === 0) {
      // This can happen because authorizedOrganizationId is null,
      // or if no workspaces are returned from the API because the user has access to no workspaces
      return;
    }

    // Never override the URL segment -- an unknown name is a genuine 404, reported by the
    // [workspaceName] layout. Just track it so leaving the route falls back to where the user was.
    if (workspaceNameFromUrl) {
      if (workspaces.some((ws) => ws.name === workspaceNameFromUrl)) {
        setPreferredWorkspaceId(workspaceNameFromUrl);
      }

      return;
    }

    if (!preferredWorkspaceId || !workspaces.some((ws) => ws.name === preferredWorkspaceId)) {
      const storedWorkspaceId = userPreferences?.[authorizedOrganizationId]?.currentWorkspaceId;

      setPreferredWorkspaceId(
        storedWorkspaceId && workspaces.some((ws) => ws.name === storedWorkspaceId)
          ? storedWorkspaceId
          : workspaces[0].name,
      );
    }
  }, [workspaces, workspaceNameFromUrl, preferredWorkspaceId, authorizedOrganizationId, userPreferences]);

  useEffect(() => {
    if (!Array.isArray(workerGroups) || workerGroups.length === 0) {
      // This can happen because authorizedOrganizationId is null,
      // or if there are no worker groups returned from the API
      return;
    }

    const defaultWorkerGroup = workerGroups?.find((group) => group.name === DEFAULT_WORKERGROUP_NAME);

    if (defaultWorkerGroup) {
      setSelectedWorkerGroup(defaultWorkerGroup);
    } else {
      setSelectedWorkerGroup(workerGroups.length > 0 ? workerGroups[0] : ({} as WorkerGroup));
    }
  }, [workerGroups]);

  const workspacesWithPathname = useMemo(() => {
    if (!Array.isArray(workspaces)) {
      return [] as WorkspaceWithPathName[];
    }

    // Enrich workspace objects with client-side pathname for routing
    return workspaces.map((ws) => ({
      ...ws,
      pathname: `/organizations/${authorizedOrganizationId}/workspaces/${ws.name}`,
    })) as WorkspaceWithPathName[];
  }, [workspaces, authorizedOrganizationId]);

  const isWorkspaceLoading = !Array.isArray(workspaces) || workspaces.length === 0;

  // Derived during render, not mirrored into state via an effect: mirror state commits a stale value
  // first, and a suspended child can delay the catch-up render indefinitely. See PLAT-12029.
  const workspace = useMemo(() => {
    const currentWorkspace = workspacesWithPathname.find((w) => w.name === workspaceId);
    // Only trust the detail response once it is for the workspace being shown, otherwise the
    // previous workspace's capabilities leak across the switch.
    const detailCapabilities = singleWorkspaceData?.name === workspaceId ? singleWorkspaceData.capabilities : undefined;

    // Do not spread a previous value: optional workspace fields (e.g. alias) must not leak across
    // switches. See PLAT-11538 / PR #13652.
    return {
      ...currentWorkspace,
      workspaceId,
      capabilities: detailCapabilities,
      workspaces: workspacesWithPathname,
      workerGroups,
      isWorkspaceLoading,
      isWorkerGroupsLoading,
      workspaceError,
      workspaceDetailError,
      workspacesError,
      selectedWorkerGroup,
      mutateWorkspaces,
      mutateWorkerGroups,
      setWorkerGroup,
      setWorkspaceId,
    } as IWorkspaceContext;
  }, [
    workspacesWithPathname,
    workspaceId,
    singleWorkspaceData,
    workerGroups,
    isWorkspaceLoading,
    isWorkerGroupsLoading,
    workspaceError,
    workspaceDetailError,
    workspacesError,
    selectedWorkerGroup,
    mutateWorkspaces,
    mutateWorkerGroups,
    setWorkerGroup,
    setWorkspaceId,
  ]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (workspaces?.some((ws) => ws.state !== AccountState.WorkspaceActive || ws.inMaintenanceMode)) {
        mutateWorkspaces();
      } else {
        clearInterval(intervalId);
      }
    }, PROVISION_STATE_FETCH_INTERVAL);

    return () => {
      clearInterval(intervalId);
    };
  }, [mutateWorkspaces, workspaces]);

  // Short-interval poll for hibernation pending states so the UI reflects the
  // Temporal workflow result within seconds rather than waiting 60s.
  useEffect(() => {
    const hibernationPendingStates = [AccountState.WorkspaceAsleepRequested, AccountState.WorkspaceWakeRequested];
    const hasHibernationPending = workspaces?.some((ws) => hibernationPendingStates.includes(ws.state as AccountState));

    if (!hasHibernationPending) {
      return;
    }

    const intervalId = setInterval(() => {
      mutateWorkspaces();
    }, HIBERNATION_STATE_FETCH_INTERVAL);

    return () => {
      clearInterval(intervalId);
    };
  }, [mutateWorkspaces, workspaces]);

  return <WorkspaceContext.Provider value={workspace}>{children}</WorkspaceContext.Provider>;
};
